import { GraphNodeType } from "@custom-types/graphs"
import { NodeType } from "@features/builder/graph/types"
import {
  getCategoryDisplayNames,
  groupGraphTagsByName,
} from "@features/builder/NodeDrawer/NodeForm/helpers"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import { decodeNodeUnit, isValidUnitFormat } from "@utils/format/nodeUnitFormat"
import { BaseGraphMetaZipNode, TKNode } from "@store/services/converter/types"

export const getDefaultValues = (
  node?: TKNode,
  nodeData?: BaseGraphMetaZipNode,
): NodeFormValues => {
  const defaultValues: NodeFormValues = {
    label: "",
    lagValue: 1,
    type: NodeType.Empty,
    comments: "",
    expression: "",
    color: "",
    historic_mnemonic: "",
    forecast_mnemonic: "",
    scenario_mnemonic: "",
    description: "",
    properties: [],
    categoryDisplayNames: {},
    sample_value: null,
    link: "",
    tags: [],
    graph_tags: [],
    unit: {
      isEmpty: true,
      usePercentageValue: false,
      decimalPlaces: "2",
      suffix: "",
    },
    attributeProperties: {
      index: [],
      columns: [],
      data: [],
    },
    exportExcluded: false,
  }

  if (!node || !nodeData) return defaultValues

  if (node.type === GraphNodeType.TABLE && "index" in node.properties) {
    defaultValues.attributeProperties = {
      index: node.properties.index,
      columns: node.properties.columns,
      data: node.properties.data,
    }
  }

  const unitFormatString = nodeData["Unit Format"] ?? ""

  // If unit is invalid, we will treat it as empty with default values.
  // User will see a warning and will be able to correct it in the form.
  // This is due to potential legacy nodes having unsupported unit formats.
  if (isValidUnitFormat(unitFormatString)) defaultValues.unit = decodeNodeUnit(unitFormatString)

  if ("df_cpd" in node.properties) {
    if (node.type === GraphNodeType.DETERMINISTIC) {
      defaultValues.expression = node.properties.df_cpd.expression?.[0] ?? ""
    } else if (
      node.type === GraphNodeType.CONTINOUS ||
      node.type === GraphNodeType.CATEGORICAL ||
      node.type === GraphNodeType.DISCRETE ||
      node.type === GraphNodeType.MIXTURE
    ) {
      const dfCpd = node.properties.df_cpd
      const distributions = dfCpd.distribution ?? []
      const propertiesNames = Object.keys(dfCpd)

      defaultValues.properties = distributions.map((_distribution, i) => {
        const entries = propertiesNames.map((property) => [property, dfCpd[property][i] ?? ""])
        return Object.fromEntries(entries)
      })

      if (node.type === GraphNodeType.CATEGORICAL) {
        defaultValues.categoryDisplayNames = getCategoryDisplayNames(node.properties)
      }
    }
  }

  defaultValues.link = nodeData.Link
  defaultValues.historic_mnemonic = nodeData["Historic Mnem"]
  defaultValues.forecast_mnemonic = nodeData["Forecast Mnem"]
  defaultValues.scenario_mnemonic = nodeData["Scenario Mnem"]
  defaultValues.description = nodeData.Description
  defaultValues.comments = nodeData.comments
  defaultValues.color = nodeData.color
  defaultValues.label = nodeData.label
  defaultValues.type = node.type as unknown as NodeType
  defaultValues.tags = (nodeData.tags ?? []).map((tag) => ({ tag: tag.Name, value: tag.Value }))
  defaultValues.graph_tags = groupGraphTagsByName(nodeData.graph_tags ?? [])

  return defaultValues
}
